// ======================================================
// FILE: WeighbridgeTicketSelector.js
// LOCATION: src/pages/dashboard_pages/weighbridge/WeighbridgeTicketSelector.js
// ======================================================
import React, { useEffect, useMemo, useState } from "react";
import {
  DataTableBody,
  DataTableHead,
  DataTableItem,
  DataTableRow,
  Icon,
  PaginationComponent,
  Button as UagButton,
} from "../../../components/Component";
import { Badge, Button, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from "reactstrap";
import { sortWeighbridgeTicketsDesc } from "../../../utils/weighbridgeTicketNormalization";
import {
  matchesWeighbridgeTicketDateFilter,
  WEIGHBRIDGE_DATE_FILTERS,
} from "../../../utils/weighbridgeTicketDateFilters";

const getDisplayValue = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  return value;
};

const resolveStatusDisplayValue = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return (
      value?.weigh_bridge_ticket_status ||
      value?.ticket_status ||
      value?.status ||
      ""
    );
  }

  return value;
};

const formatTicketStatus = (value) => {
  const normalizedValue = getDisplayValue(resolveStatusDisplayValue(value));
  if (normalizedValue === "--") return normalizedValue;
  if (String(normalizedValue).toUpperCase() === "GOODS_RECEIVED_NOTE") return "GOODS RECEIVED";
  return String(normalizedValue).replaceAll("_", " ");
};

const formatDesignation = (value) => {
  const normalizedValue = getDisplayValue(value);
  if (normalizedValue === "--") return normalizedValue;
  return String(normalizedValue).replaceAll("_", " ");
};

const getTicketStatusTone = (value) => {
  const normalizedValue = String(resolveStatusDisplayValue(value) || "").toLowerCase();
  if (
    normalizedValue.includes("complete") ||
    normalizedValue.includes("delivered") ||
    normalizedValue.includes("received")
  )
  {
    return "success";
  }

  if (
    normalizedValue.includes("processing") ||
    normalizedValue.includes("transit") ||
    normalizedValue.includes("offloading") ||
    normalizedValue.includes("onboarding")
  )
  {
    return "warning";
  }

  return "secondary";
};

const getUsageTone = (usageStatus) =>
  String(usageStatus || "").toUpperCase() === "USED" ? "danger" : "success";

const getUsageLabel = (usageStatus) =>
  String(usageStatus || "").toUpperCase() === "USED" ? "Used" : "Available";

const defaultIsSelectableTicket = (ticket = {}) =>
  String(ticket?.usage_status || "").toUpperCase() !== "USED";

const defaultGetDisabledActionLabel = (ticket = {}) =>
  String(ticket?.usage_status || "").toUpperCase() === "USED" ? "Used" : "Unavailable";

const defaultGetSelectionStatusLabel = (ticket = {}) =>
  String(ticket?.usage_status || "").toUpperCase() === "USED" ? "Already used" : "Eligible";

const defaultGetSelectionStatusTone = (ticket = {}) =>
  String(ticket?.usage_status || "").toUpperCase() === "USED" ? "warning" : "success";

const WeighbridgeTicketSelector = ({
  tickets = [],
  allTickets = [],
  onSelect = () => { },
  onClose = () => { },
  onRefresh = null,
  isRefreshing = false,
  isSelectableTicket = defaultIsSelectableTicket,
  getDisabledActionLabel = defaultGetDisabledActionLabel,
  getSelectionStatusLabel = defaultGetSelectionStatusLabel,
  getSelectionStatusTone = defaultGetSelectionStatusTone,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("All Statuses");
  const [selectedDateFilter, setSelectedDateFilter] = useState("All Dates");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage] = useState(10);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const formatDate = (iso) => {
    if (!iso) return "--";

    try
    {
      return new Date(iso).toLocaleString();
    } catch (error)
    {
      return "--";
    }
  };

  const statusFilters = useMemo(() => {
    const uniqueStatuses = Array.from(
      new Set(
        tickets
          .map((ticket) => resolveStatusDisplayValue(ticket?.weigh_bridge_ticket_status))
          .filter((value) => value && value !== "--"),
      ),
    );

    return ["All Statuses", ...uniqueStatuses];
  }, [tickets]);

  const filteredTickets = useMemo(() => {
    let nextTickets = [...tickets];

    if (selectedStatusFilter !== "All Statuses") {
      nextTickets = nextTickets.filter(
        (ticket) => resolveStatusDisplayValue(ticket?.weigh_bridge_ticket_status) === selectedStatusFilter,
      );
    }

    if (selectedDateFilter !== "All Dates") {
      nextTickets = nextTickets.filter((ticket) =>
        matchesWeighbridgeTicketDateFilter(ticket, selectedDateFilter),
      );
    }

    if (searchTerm.trim()) {
      const loweredSearch = searchTerm.toLowerCase();
      nextTickets = nextTickets.filter((ticket) =>
        [
          ticket?.weigh_bridge_ticket_number,
          ticket?.weigh_bridge_ticket_reference_no,
          ticket?.external_weigh_bridge_ticket_number,
          ticket?.truck_licence_plate,
          ticket?.designation,
          ticket?.truck_driver_name,
          ticket?.transporter_name,
          resolveStatusDisplayValue(ticket?.weigh_bridge_ticket_status),
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(loweredSearch)),
      );
    }

    return sortWeighbridgeTicketsDesc(nextTickets).sort((leftTicket, rightTicket) => {
      const leftIsSelectable = isSelectableTicket(leftTicket);
      const rightIsSelectable = isSelectableTicket(rightTicket);

      if (leftIsSelectable === rightIsSelectable) return 0;

      return leftIsSelectable ? -1 : 1;
    });
  }, [isSelectableTicket, searchTerm, selectedDateFilter, selectedStatusFilter, tickets]);

  const selectableTicketCount = filteredTickets.filter((ticket) => isSelectableTicket(ticket)).length;
  const blockedTicketCount = filteredTickets.length - selectableTicketCount;

  const indexOfLastItem = currentPage * itemPerPage;
  const indexOfFirstItem = indexOfLastItem - itemPerPage;
  const currentItems = filteredTickets.slice(indexOfFirstItem, indexOfLastItem);
  const hasActiveFilters =
    searchTerm.trim() !== "" ||
    selectedStatusFilter !== "All Statuses" ||
    selectedDateFilter !== "All Dates";

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedDateFilter, selectedStatusFilter]);

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedStatusFilter("All Statuses");
    setSelectedDateFilter("All Dates");
    setCurrentPage(1);
  };

  return (
    <div>
      <div className="border rounded-3 bg-light p-3 p-md-4 mb-3">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
          <div>
            <h5 className="mb-1">Weighbridge Tickets</h5>
            <div className="text-soft">
              Selectable tickets can be used for the current workflow. Other tickets remain visible for reference.
            </div>
          </div>

          <div className="d-flex flex-wrap gap-2">
            <span className="badge badge-dim bg-primary">
              Selectable: <span className="ms-1">{selectableTicketCount}</span>
            </span>
            <span className="badge badge-dim bg-danger">
              Not Selectable: <span className="ms-1">{blockedTicketCount}</span>
            </span>
            <span className="badge badge-dim bg-light text-dark border">
              Total Loaded: <span className="ms-1">{allTickets?.length || filteredTickets.length}</span>
            </span>
          </div>
        </div>

        <div className="d-flex justify-content-end mt-3 gap-2">
          {typeof onRefresh === "function" && (
            <div>
              <UagButton
                color="light"
                outline
                className="btn-white btn-sm px-3"
                onClick={onRefresh}
                disabled={isRefreshing}
              >
                <Icon name="repeat" />
                <span>{isRefreshing ? "Refreshing..." : "Refresh"}</span>
              </UagButton>
            </div>
          )}
          <div>
            <UagButton color="light" outline className="btn-white btn-sm px-3" onClick={onClose}>
              <span>Close</span>
            </UagButton>
          </div>
        </div>
      </div>

      {tickets.length > 0 ? (
        <>
          <ul className="nk-block-tools g-3 d-flex justify-end mb-2">
            <li>
              <div>
                <div className="form-icon form-icon-right">
                  <Icon name="search"></Icon>
                </div>
                <input
                  type="text"
                  className="form-control"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search for Tickets"
                />
              </div>
            </li>
            <li className="preview-item">
              <UncontrolledDropdown>
                <div className="btn-group">
                  <DropdownToggle color="light" outline className="dropdown-toggle-split btn-white">
                    <Icon name="chevron-down"></Icon>
                    <span>{selectedStatusFilter}</span>
                  </DropdownToggle>
                </div>
                <DropdownMenu className="mt-3">
                  <ul className="link-list-opt">
                    {statusFilters.map((status) => (
                      <li key={status}>
                        <DropdownItem
                          tag="a"
                          href="#status-filter"
                          onClick={(event) => {
                            event.preventDefault();
                            setSelectedStatusFilter(status);
                          }}
                        >
                          <span>{status}</span>
                        </DropdownItem>
                      </li>
                    ))}
                  </ul>
                </DropdownMenu>
              </UncontrolledDropdown>
            </li>
            <li className="preview-item">
              <UncontrolledDropdown>
                <div className="btn-group">
                  <DropdownToggle color="light" outline className="dropdown-toggle-split btn-white">
                    <Icon name="calendar"></Icon>
                    <span>{selectedDateFilter}</span>
                  </DropdownToggle>
                </div>
                <DropdownMenu className="mt-3">
                  <ul className="link-list-opt">
                    {WEIGHBRIDGE_DATE_FILTERS.map((dateFilter) => (
                      <li key={dateFilter}>
                        <DropdownItem
                          tag="a"
                          href="#date-filter"
                          onClick={(event) => {
                            event.preventDefault();
                            setSelectedDateFilter(dateFilter);
                          }}
                        >
                          <span>{dateFilter}</span>
                        </DropdownItem>
                      </li>
                    ))}
                  </ul>
                </DropdownMenu>
              </UncontrolledDropdown>
            </li>
            <li>
              <Button
                type="button"
                color="light"
                outline
                className="btn-white"
                onClick={clearFilters}
                disabled={!hasActiveFilters}
              >
                Clear Filters
              </Button>
            </li>
          </ul>

          <div className="nk-tb-list is-separate is-medium mt-1 mb-3">
          <div className="card-inner p-0">
            <DataTableBody>
              <DataTableHead>
                <DataTableRow size="sm">
                  <span>Ticket Ref No.</span>
                </DataTableRow>
                <DataTableRow>
                  <span>External Ref No.</span>
                </DataTableRow>
                <DataTableRow>
                  <span>Truck Plate</span>
                </DataTableRow>
                <DataTableRow>
                  <span>Designation</span>
                </DataTableRow>
                <DataTableRow>
                  <span>Ticket Status</span>
                </DataTableRow>
                <DataTableRow>
                  <span>Availability</span>
                </DataTableRow>
                <DataTableRow>
                  <span>GRN Eligibility</span>
                </DataTableRow>
                <DataTableRow>
                  <span>Driver / Transporter</span>
                </DataTableRow>
                <DataTableRow size="md">
                  <span>Created On</span>
                </DataTableRow>
                <DataTableRow size="md">
                  <span>Action</span>
                </DataTableRow>
              </DataTableHead>

              {currentItems.map((ticket) => {
                const canSelectTicket = isSelectableTicket(ticket);
                const disabledActionLabel = getDisabledActionLabel(ticket);
                const selectionStatusLabel = getSelectionStatusLabel(ticket);
                const selectionStatusTone = getSelectionStatusTone(ticket);

                return (
                <DataTableItem
                  key={ticket.id}
                  className={
                    !canSelectTicket
                      ? "opacity-75"
                      : ""
                  }
                >
                  <DataTableRow size="sm">
                    <span className="tb-product">
                      <span className="title">
                        {ticket?.weigh_bridge_ticket_reference_no || ticket?.weigh_bridge_ticket_number || "--"}
                      </span>
                    </span>
                    <div>
                      <span className="fw-medium">Ticket No: </span>
                      <span className="tb-sub">{ticket?.weigh_bridge_ticket_number || "--"}</span>
                    </div>
                  </DataTableRow>

                  <DataTableRow>
                    <span className="tb-sub">
                      {ticket?.external_weigh_bridge_ticket_number || "--"}
                    </span>
                  </DataTableRow>

                  <DataTableRow>
                    <span className="tb-sub">
                      {ticket?.truck_licence_plate || "--"}
                    </span>
                  </DataTableRow>

                  <DataTableRow>
                    <span className="tb-sub">
                      {formatDesignation(ticket?.designation)}
                    </span>
                  </DataTableRow>

                  <DataTableRow>
                    <span className="tb-odr-status">
                      <Badge color={getTicketStatusTone(ticket?.weigh_bridge_ticket_status)} className="badge-dot">
                        {formatTicketStatus(ticket?.weigh_bridge_ticket_status)}
                      </Badge>
                    </span>
                  </DataTableRow>

                  <DataTableRow>
                    <span className="tb-odr-status">
                      <Badge color={getUsageTone(ticket?.usage_status)} className="badge-dot">
                        {getUsageLabel(ticket?.usage_status)}
                      </Badge>
                    </span>
                  </DataTableRow>

                  <DataTableRow>
                    <span className="tb-odr-status">
                      <Badge color={selectionStatusTone} className="badge-dot">
                        {selectionStatusLabel}
                      </Badge>
                    </span>
                  </DataTableRow>

                  <DataTableRow>
                    <div>
                      <span className="tb-sub d-block">{ticket?.truck_driver_name || "--"}</span>
                      <span className="tb-sub text-soft">{ticket?.transporter_name || "--"}</span>
                    </div>
                  </DataTableRow>

                  <DataTableRow size="md">
                    <span className="tb-sub">
                      {formatDate(ticket?.created_on || ticket?.time_in)}
                    </span>
                  </DataTableRow>

                  <DataTableRow className="nk-tb-col-tools">
                    <div className="tb-odr-btns d-none d-sm-inline">
                      {!canSelectTicket ? (
                        <Button
                          type="button"
                          color="light"
                          size="sm"
                          className="btn btn-dim"
                          disabled
                        >
                          {disabledActionLabel}
                        </Button>
                      ) : (
                      <Button
                        type="button"
                        color="primary"
                        size="sm"
                        className="btn btn-dim"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onSelect(ticket);
                        }}
                      >
                        Select
                      </Button>
                      )}
                    </div>

                    {!canSelectTicket ? (
                      <Button
                        type="button"
                        color="light"
                        size="sm"
                        className="btn-pd-auto d-sm-none"
                        disabled
                      >
                        <Icon name="lock" />
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        color="primary"
                        size="sm"
                        className="btn-pd-auto d-sm-none"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onSelect(ticket);
                        }}
                      >
                        <Icon name="chevron-right" />
                      </Button>
                    )}
                  </DataTableRow>
                </DataTableItem>
                );
              })}
            </DataTableBody>

            {currentItems.length === 0 && (
              <div className="p-4 text-center text-soft fw-bold border-top">
                No matching weighbridge tickets found.
              </div>
            )}

            {filteredTickets.length > itemPerPage && (
              <div className="mt-3">
                <PaginationComponent
                  itemPerPage={itemPerPage}
                  totalItems={filteredTickets.length}
                  paginate={paginate}
                  currentPage={currentPage}
                />
              </div>
            )}
          </div>
        </div>
        </>
      ) : (
        <div className="p-4 text-center text-soft fw-bold">
          No weighbridge tickets found.
        </div>
      )}
    </div>
  );
};

export default WeighbridgeTicketSelector;
